import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import {
  ExternalLink, Search, ChevronDown, ChevronUp, Loader2,
  Mail, Package, AlertCircle, RotateCcw, FileText,
} from "lucide-react";

const SCORE_THRESHOLDS = { HIGH: 50, MEDIUM: 30 };

const getScoreColor = (score) => {
  if (score >= SCORE_THRESHOLDS.HIGH) return 'text-green-600';
  if (score >= SCORE_THRESHOLDS.MEDIUM) return 'text-yellow-600';
  return 'text-zinc-500';
};

const STATUS_COLORS = {
  pending_payment: "bg-yellow-100 text-yellow-800",
  paid: "bg-blue-100 text-blue-800",
  in_progress: "bg-purple-100 text-purple-800",
  completed: "bg-green-100 text-green-800",
  delivered: "bg-gray-100 text-gray-800",
  redo_requested: "bg-red-100 text-red-800",
};

const TEMPLATE_BUTTONS = [
  { key: 'partial', label: 'Partial Delivery', color: 'bg-amber-500 hover:bg-amber-600' },
  { key: 'refining', label: 'Refining Search', color: 'bg-blue-500 hover:bg-blue-600' },
  { key: 'redo', label: 'Redo Notice', color: 'bg-purple-500 hover:bg-purple-600' },
  { key: 'strategic', label: 'Strategic Fallback', color: 'bg-zinc-500 hover:bg-zinc-600' },
];

export function SubmissionRow({
  sub,
  isExpanded,
  jobs,
  fetchingJobs,
  sendingDelivery,
  sendingTemplate,
  onToggleExpand,
  onFetchJobs,
  onSendDelivery,
  onSendTemplate,
  onUpdateStatus,
  onUpdateField,
}) {
  return (
    <div className="p-4" data-testid={`submission-${sub.submission_id}`}>
      {/* Main row */}
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => onToggleExpand(sub.submission_id)}
      >
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <span className="font-medium text-zinc-900">{sub.full_name}</span>
            <Badge className={STATUS_COLORS[sub.status] || "bg-gray-100 text-gray-800"}>{sub.status}</Badge>
            <span className="text-sm text-zinc-500">{sub.plan} - ${sub.amount / 100}</span>
          </div>
          <p className="text-sm text-zinc-600 mt-1">
            {sub.target_role} - {sub.location} - {sub.job_type}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-400">
            {new Date(sub.created_at).toLocaleDateString()}
          </span>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <SubmissionDetails
          sub={sub}
          jobs={jobs}
          fetchingJobs={fetchingJobs}
          sendingDelivery={sendingDelivery}
          sendingTemplate={sendingTemplate}
          onFetchJobs={onFetchJobs}
          onSendDelivery={onSendDelivery}
          onSendTemplate={onSendTemplate}
          onUpdateStatus={onUpdateStatus}
          onUpdateField={onUpdateField}
        />
      )}
    </div>
  );
}

function SubmissionDetails({
  sub, jobs, fetchingJobs, sendingDelivery, sendingTemplate,
  onFetchJobs, onSendDelivery, onSendTemplate, onUpdateStatus, onUpdateField,
}) {
  return (
    <div className="mt-4 pt-4 border-t border-zinc-100">
      {/* Info grid */}
      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <ContactInfo sub={sub} />
        <PreferencesInfo sub={sub} />
        <AdminControls sub={sub} onUpdateField={onUpdateField} />
      </div>

      {/* Internal Notes */}
      <div className="mb-4">
        <p className="text-xs text-zinc-500 uppercase mb-1">Internal Notes (admin only)</p>
        <Textarea
          value={sub.internal_notes || ''}
          onChange={(e) => onUpdateField(sub.submission_id, 'internal_notes', e.target.value)}
          placeholder="Add notes about this submission..."
          className="rounded-sm text-sm h-16"
        />
      </div>

      {/* Action buttons */}
      <ActionButtons
        sub={sub}
        fetchingJobs={fetchingJobs}
        sendingDelivery={sendingDelivery}
        onFetchJobs={onFetchJobs}
        onSendDelivery={onSendDelivery}
        onUpdateStatus={onUpdateStatus}
      />

      {/* Template Emails */}
      <TemplateEmailPanel
        sub={sub}
        sendingTemplate={sendingTemplate}
        onSendTemplate={onSendTemplate}
      />

      {/* Matched Jobs */}
      {jobs && jobs.length > 0 && <JobsTable jobs={jobs} />}
    </div>
  );
}

function ContactInfo({ sub }) {
  return (
    <div>
      <p className="text-xs text-zinc-500 uppercase mb-1">Contact</p>
      <p className="text-sm">{sub.email}</p>
      {sub.phone && <p className="text-sm">{sub.phone}</p>}
      {sub.linkedin_url && (
        <a href={sub.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#002FA7] hover:underline flex items-center gap-1">
          LinkedIn <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
}

function PreferencesInfo({ sub }) {
  return (
    <div>
      <p className="text-xs text-zinc-500 uppercase mb-1">Preferences</p>
      <p className="text-sm">Experience: {sub.experience_level}</p>
      <p className="text-sm">Priority: <Badge className={sub.priority === 'urgent' ? 'bg-red-100 text-red-800' : 'bg-zinc-100 text-zinc-800'}>{sub.priority || 'exploring'}</Badge></p>
      {sub.target_companies && <p className="text-sm">Companies: {sub.target_companies}</p>}
      {sub.notes && <p className="text-sm text-zinc-600">Notes: {sub.notes}</p>}
    </div>
  );
}

function AdminControls({ sub, onUpdateField }) {
  return (
    <div>
      <p className="text-xs text-zinc-500 uppercase mb-1">Admin Controls</p>
      <div className="space-y-2">
        <Select
          value={sub.delivery_type || 'full'}
          onValueChange={(v) => onUpdateField(sub.submission_id, 'delivery_type', v)}
        >
          <SelectTrigger className="h-8 text-xs rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="full">Full Delivery (20-50 jobs)</SelectItem>
            <SelectItem value="partial">Partial (5-15 jobs)</SelectItem>
            <SelectItem value="strategic">Strategic (guidance only)</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Checkbox
            id={`qc-${sub.submission_id}`}
            checked={sub.quality_checked || false}
            onCheckedChange={(checked) => onUpdateField(sub.submission_id, 'quality_checked', checked)}
          />
          <label htmlFor={`qc-${sub.submission_id}`} className="text-xs text-zinc-700">Quality Checked</label>
        </div>
      </div>
    </div>
  );
}

function ActionButtons({ sub, fetchingJobs, sendingDelivery, onFetchJobs, onSendDelivery, onUpdateStatus }) {
  return (
    <div className="flex flex-wrap gap-2 mb-4">
      <Button size="sm" variant="outline" onClick={() => onFetchJobs(sub.submission_id)} disabled={fetchingJobs} className="rounded-sm">
        {fetchingJobs ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Fetching...</> : <><Search className="w-4 h-4 mr-1" /> Fetch Jobs</>}
      </Button>

      {sub.matched_jobs?.length > 0 && sub.status !== "delivered" && (
        <Button size="sm" onClick={() => onSendDelivery(sub.submission_id)} disabled={sendingDelivery || !sub.quality_checked} className="rounded-sm bg-green-600 hover:bg-green-700" title={!sub.quality_checked ? "Check 'Quality Checked' first" : ""}>
          {sendingDelivery ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending...</> : <><Package className="w-4 h-4 mr-1" /> Send Delivery Email</>}
        </Button>
      )}

      {sub.status === "paid" && <Button size="sm" onClick={() => onUpdateStatus(sub.submission_id, "in_progress")} className="rounded-sm bg-purple-600 hover:bg-purple-700">Mark In Progress</Button>}
      {sub.status === "in_progress" && <Button size="sm" onClick={() => onUpdateStatus(sub.submission_id, "completed")} className="rounded-sm bg-blue-600 hover:bg-blue-700">Mark Completed</Button>}
      {sub.status === "completed" && <Button size="sm" onClick={() => onUpdateStatus(sub.submission_id, "delivered")} className="rounded-sm bg-zinc-600 hover:bg-zinc-700">Mark Delivered (Manual)</Button>}
      {sub.status === "delivered" && <Badge className="bg-green-100 text-green-800 flex items-center gap-1"><Mail className="w-3 h-3" /> Delivered</Badge>}

      {(sub.status === "delivered" || sub.status === "completed") && (
        <Button size="sm" variant="outline" onClick={() => onUpdateStatus(sub.submission_id, "redo_requested")} className="rounded-sm border-red-300 text-red-600 hover:bg-red-50">
          <RotateCcw className="w-4 h-4 mr-1" /> Mark for Redo
        </Button>
      )}
      {sub.status === "redo_requested" && <Badge className="bg-red-100 text-red-800 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Redo Requested</Badge>}
    </div>
  );
}

function TemplateEmailPanel({ sub, sendingTemplate, onSendTemplate }) {
  return (
    <div className="mb-4 p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
      <p className="text-xs text-zinc-500 uppercase mb-2 flex items-center gap-1">
        <FileText className="w-3 h-3" /> Send Template Email
      </p>
      <div className="flex flex-wrap gap-2">
        {TEMPLATE_BUTTONS.map((tmpl) => (
          <Button
            key={tmpl.key}
            size="sm"
            onClick={() => onSendTemplate(sub.submission_id, tmpl.key)}
            disabled={sendingTemplate === tmpl.key}
            className={`rounded-sm text-white ${tmpl.color}`}
          >
            {sendingTemplate === tmpl.key ? <Loader2 className="w-3 h-3 animate-spin" /> : tmpl.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

function JobsTable({ jobs }) {
  return (
    <div className="mt-4">
      <p className="text-xs text-zinc-500 uppercase mb-2">Matched Jobs ({jobs.length})</p>
      <div className="max-h-64 overflow-y-auto border border-zinc-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 sticky top-0">
            <tr>
              <th className="text-left p-2 font-medium">Title</th>
              <th className="text-left p-2 font-medium">Company</th>
              <th className="text-left p-2 font-medium">Source</th>
              <th className="text-left p-2 font-medium">Score</th>
              <th className="text-left p-2 font-medium">Link</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {jobs.map((job) => (
              <tr key={job.external_id || job.url || job.title} className="hover:bg-zinc-50">
                <td className="p-2">{job.title?.substring(0, 40)}{job.title?.length > 40 ? '...' : ''}</td>
                <td className="p-2">{job.company || '-'}</td>
                <td className="p-2"><Badge variant="outline" className="text-xs">{job.source}</Badge></td>
                <td className="p-2"><span className={`font-medium ${getScoreColor(job.score)}`}>{job.score}</span></td>
                <td className="p-2">
                  <a href={job.apply_url || job.url} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
